import { ChatModel, ChatModelType } from 'types';

import { gemini4ArgonMultiplier } from '@/constants/gemini4Argon';

import AnthropicSvg from '~/svg/Anthropic.svg';
import DeepseekSvg from '~/svg/deepseek.svg';
import GoogleSvg from '~/svg/google.svg';
import MiniMaxSvg from '~/svg/Hailuo.svg';
import MetaSvg from '~/svg/meta.svg';
import MistralAISvg from '~/svg/mistral.svg';
import MoonshotSvg from '~/svg/moonshot.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import PerplexitySvg from '~/svg/perplexity.svg';
import XAISvg from '~/svg/xai.svg';
import ZAISvg from '~/svg/zai.svg';

export const chatModels: readonly ChatModel[] = [
  // GPT-6 Astra: $10/$50 per 1M (2x the GPT-5.6 Sol tier), 1.05M context,
  // 128K output. Reasoning is ALWAYS on (effort 'none' is rejected) and
  // sampling params (temperature/top_p) are rejected — see
  // MODELS_WITHOUT_TEMPERATURE in @/constants/common.ts.
  {
    id: 6001,
    title: 'GPT-6 Astra',
    slug: 'gpt-6-astra',
    description:
      "OpenAI's most capable model, built for the hardest end-to-end work: complex reasoning, coding, computer use, research, and document creation.",
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 6011,
    title: 'GPT-6 Astra (Max)',
    slug: 'gpt-6-astra-max',
    description:
      'GPT-6 Astra at maximum reasoning effort for the hardest professional, coding, research, and long-horizon agentic tasks.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  // GPT-6.1 Sol (2026-09-29): the upgraded Sol tier — $2 / $0.10 cached /
  // $10 per 1M (one fifth of Astra), 1.05M context, 128K output. Effort
  // low…max with medium default; 'none' is rejected, like the rest of GPT-6.
  // Billed like GPT-6 Sol (0.5x).
  {
    id: 6103,
    title: 'GPT-6.1 Sol',
    slug: 'gpt-6.1-sol',
    description:
      "OpenAI's upgraded Sol model (September 2026). Stronger agentic coding and computer use than GPT-6 Sol at the same price, close to GPT-6 Astra on coding at a fraction of the cost.",
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 6113,
    title: 'GPT-6.1 Sol (Max)',
    slug: 'gpt-6.1-sol-max',
    description:
      'GPT-6.1 Sol at maximum reasoning effort for complex coding, computer use, and long-horizon agentic tasks.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 6003,
    title: 'GPT-6 Sol',
    slug: 'gpt-6-sol',
    description:
      "Brings much of GPT-6 Astra's strength into a faster, more affordable model for work at scale — at half the price of GPT-5.6 Sol.",
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 6013,
    title: 'GPT-6 Sol (Max)',
    slug: 'gpt-6-sol-max',
    description:
      'GPT-6 Sol at maximum reasoning effort for complex professional, coding, and long-horizon agentic tasks.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 6004,
    title: 'GPT-6 Luna',
    slug: 'gpt-6-luna',
    description:
      'The fastest and most affordable GPT-6 model, built on the advances behind GPT-6 Astra for cost-sensitive, high-volume work.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.1,
  },
  {
    id: 6014,
    title: 'GPT-6 Luna (Max)',
    slug: 'gpt-6-luna-max',
    description:
      'GPT-6 Luna at maximum reasoning effort for affordable tasks that need substantially deeper analysis.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.1,
  },
  {
    id: 5601,
    title: 'GPT-5.6 Sol',
    slug: 'gpt-5.6-sol',
    description:
      "OpenAI's frontier GPT-5.6 model for complex professional work, coding, research, and demanding agentic tasks.",
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 5611,
    title: 'GPT-5.6 Sol (Max)',
    slug: 'gpt-5.6-sol-max',
    description:
      'GPT-5.6 Sol at maximum reasoning effort for the hardest professional, coding, research, and long-horizon agentic tasks.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 5602,
    title: 'GPT-5.6 Terra',
    slug: 'gpt-5.6-terra',
    description:
      'A balanced GPT-5.6 model with strong intelligence at half the token price of Sol. Built for everyday professional workloads.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 5612,
    title: 'GPT-5.6 Terra (Max)',
    slug: 'gpt-5.6-terra-max',
    description:
      'GPT-5.6 Terra at maximum reasoning effort for complex professional work that benefits from deeper analysis.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 5603,
    title: 'GPT-5.6 Luna',
    slug: 'gpt-5.6-luna',
    description:
      'The fastest and most affordable GPT-5.6 model, optimized for cost-sensitive, high-volume workloads.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.2,
  },
  {
    id: 5613,
    title: 'GPT-5.6 Luna (Max)',
    slug: 'gpt-5.6-luna-max',
    description:
      'GPT-5.6 Luna at maximum reasoning effort for affordable tasks that need substantially deeper analysis.',
    contextLength: 1050000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.2,
  },
  {
    id: 11115,
    title: 'Gemini 3.8 Flash',
    slug: 'gemini-3.8-flash',
    description:
      "Google's newest Flash model — another jump in agentic and coding capability over 3.7 Flash at the same Flash-tier speed and cost. Thinking model with multi-level reasoning.",
    contextLength: 1000000,
    messageLimit: 100000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: true,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.25,
  },
  {
    id: 11111,
    title: 'Gemini 3 Flash',
    slug: 'gemini-3-flash-preview',
    description:
      'Fast and versatile new generation model for scaling across diverse tasks.',
    contextLength: 1000000,
    messageLimit: 100000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.25,
  },
  {
    id: 5501,
    title: 'GPT-5.5 Instant',
    slug: 'gpt-5.5-instant',
    description:
      "OpenAI's newest frontier model for everyday work. Faster, smarter, and more efficient with native computer use and a 1M context window.",
    contextLength: 1000000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    id: 5401,
    title: 'GPT-5.4 Instant',
    slug: 'gpt-5.4-instant',
    description:
      'The most capable and efficient frontier model for everyday work. Native computer use, improved token efficiency, and 1M context window.',
    contextLength: 1000000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 2349,
    title: 'GLM 5.3 Flash',
    slug: 'glm-5p3-flash',
    description:
      "Z.ai's first native multimodal model in the GLM-5 series. Combines a 1M-token context window with always-on reasoning and efficient long-horizon coding and agentic performance, with image input support.",
    contextLength: 1000000,
    messageLimit: 131072,
    icon: ZAISvg,
    provider: 'Z.ai',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.05,
  },
  {
    id: 2352,
    title: 'GLM 5.3 Flash (Max)',
    slug: 'glm-5p3-flash-max',
    description:
      'GLM 5.3 Flash at its deepest thinking budget. Slower to answer and spends more words on reasoning — use it when a task needs more thoroughness than the standard Flash setting gives.',
    contextLength: 1000000,
    messageLimit: 131072,
    icon: ZAISvg,
    provider: 'Z.ai',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.05,
  },
  {
    id: 2351,
    title: 'GLM 5.3 (Max)',
    slug: 'glm-5p3-max',
    description:
      'GLM 5.3 at its deepest thinking budget. Use for the hardest coding and long-horizon agentic problems where thoroughness matters more than latency.',
    contextLength: 1000000,
    messageLimit: 100000,
    icon: ZAISvg,
    provider: 'Z.ai',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 0.25,
  },
  {
    id: 2350,
    title: 'GLM 5.3',
    slug: 'glm-5p3',
    description:
      "Z.ai's newest flagship model in the GLM-5 series, with a 1M-token context window and always-on reasoning. Improves on GLM 5.2 for complex coding and long-horizon agentic tasks.",
    contextLength: 1000000,
    messageLimit: 100000,
    icon: ZAISvg,
    provider: 'Z.ai',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 0.25,
  },
  {
    id: 82,
    title: 'Claude Fable 5.1',
    slug: 'claude-fable-5-1',
    description:
      "Anthropic's most capable widely released model — the successor to Fable 5 at the same price. Adaptive thinking is always on with selectable effort levels. 1M context window and 128K output.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 2,
  },
  {
    // Legacy alias — kept so saved chats still resolve. Maps to High effort on the backend.
    id: 83,
    title: 'Claude Fable 5.1 Reasoning',
    slug: 'claude-fable-5-1-reasoning',
    description:
      'Reasoning-enabled variant of Claude Fable 5.1. Adaptive extended thinking for the hardest coding, math, and agentic tasks.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    hidden: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 821,
    title: 'Claude Fable 5.1 Reasoning (Low)',
    slug: 'claude-fable-5-1-reasoning-low',
    description:
      'Fable 5.1 with minimal adaptive thinking. Fastest and cheapest reasoning variant — good for quick answers that still benefit from a short chain of thought.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 822,
    title: 'Claude Fable 5.1 Reasoning (Medium)',
    slug: 'claude-fable-5-1-reasoning-medium',
    description:
      'Fable 5.1 with balanced adaptive thinking. Good depth for typical reasoning tasks without the latency of the deeper variants.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 823,
    title: 'Claude Fable 5.1 Reasoning (High)',
    slug: 'claude-fable-5-1-reasoning-high',
    description:
      "Fable 5.1's default reasoning depth. Deep adaptive thinking for complex coding, research, and multi-step agent work.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 824,
    title: 'Claude Fable 5.1 Reasoning (Max)',
    slug: 'claude-fable-5-1-reasoning-max',
    description:
      'Fable 5.1 at its deepest reasoning setting. Use for the hardest problems where thoroughness matters more than latency — SWE-bench-grade coding, advanced math, and long-horizon agents.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 76,
    title: 'Claude Fable 5',
    slug: 'claude-fable-5',
    description:
      "Anthropic's most capable widely released model (June 2026), built for the most demanding reasoning and long-horizon agentic work. Adaptive thinking is always on with selectable effort levels. 1M context window and 128K output.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 2,
  },
  {
    // Legacy alias — kept so saved chats still resolve. Maps to High effort on the backend.
    id: 77,
    title: 'Claude Fable 5 Reasoning',
    slug: 'claude-fable-5-reasoning',
    description:
      'Reasoning-enabled variant of Claude Fable 5. Adaptive extended thinking for the hardest coding, math, and agentic tasks.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    hidden: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 761,
    title: 'Claude Fable 5 Reasoning (Low)',
    slug: 'claude-fable-5-reasoning-low',
    description:
      'Fable 5 with minimal adaptive thinking. Fastest and cheapest reasoning variant — good for quick answers that still benefit from a short chain of thought.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 762,
    title: 'Claude Fable 5 Reasoning (Medium)',
    slug: 'claude-fable-5-reasoning-medium',
    description:
      'Fable 5 with balanced adaptive thinking. Good depth for typical reasoning tasks without the latency of the deeper variants.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 763,
    title: 'Claude Fable 5 Reasoning (High)',
    slug: 'claude-fable-5-reasoning-high',
    description:
      "Fable 5's default reasoning depth. Deep adaptive thinking for complex coding, research, and multi-step agent work.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 764,
    title: 'Claude Fable 5 Reasoning (Max)',
    slug: 'claude-fable-5-reasoning-max',
    description:
      'Fable 5 at its deepest reasoning setting. Use for the hardest problems where thoroughness matters more than latency — SWE-bench-grade coding, advanced math, and long-horizon agents.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 2,
  },
  {
    id: 84,
    title: 'Claude Opus 5.5',
    slug: 'claude-opus-5-5',
    description:
      "Anthropic's newest Opus (September 2026) — the successor to Opus 5 at a lower price. Adaptive thinking is always on (this variant runs it at low effort for speed). 1M context window and 128K output.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    id: 842,
    title: 'Claude Opus 5.5 Reasoning (Medium)',
    slug: 'claude-opus-5-5-reasoning-medium',
    description:
      "Opus 5.5 with balanced adaptive thinking — the model's own default depth. Good for typical reasoning tasks without the latency of the deeper variants.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 843,
    title: 'Claude Opus 5.5 Reasoning (High)',
    slug: 'claude-opus-5-5-reasoning-high',
    description:
      'Opus 5.5 with deep adaptive thinking for complex coding, research, and multi-step agent work.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 844,
    title: 'Claude Opus 5.5 Reasoning (Max)',
    slug: 'claude-opus-5-5-reasoning-max',
    description:
      'Opus 5.5 at its deepest reasoning setting. Use for the hardest problems where thoroughness matters more than latency — advanced coding, math, and long-horizon agents.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 80,
    title: 'Claude Opus 5',
    slug: 'claude-opus-5',
    description:
      "Anthropic's flagship Opus model (July 2026) — a step change over Opus 4.8 on deep reasoning, agentic coding, and long-horizon work, at the same price. 1M context, 128K output, and adaptive thinking with selectable effort levels.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    // Legacy alias — kept so saved chats still resolve. Maps to High effort on the backend.
    id: 81,
    title: 'Claude Opus 5 Reasoning',
    slug: 'claude-opus-5-reasoning',
    description:
      'Reasoning-enabled variant of Claude Opus 5. Adaptive extended thinking for the hardest coding, math, and agentic tasks.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    hidden: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 801,
    title: 'Claude Opus 5 Reasoning (Low)',
    slug: 'claude-opus-5-reasoning-low',
    description:
      'Opus 5 with minimal adaptive thinking. Fastest and cheapest reasoning variant — unusually strong on this model, so try it before reaching for a deeper setting.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 802,
    title: 'Claude Opus 5 Reasoning (Medium)',
    slug: 'claude-opus-5-reasoning-medium',
    description:
      'Opus 5 with balanced adaptive thinking. Good depth for typical reasoning tasks without the latency of the deeper variants.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 803,
    title: 'Claude Opus 5 Reasoning (High)',
    slug: 'claude-opus-5-reasoning-high',
    description:
      "Opus 5's default reasoning depth. Deep adaptive thinking for complex coding, research, and multi-step agent work.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 804,
    title: 'Claude Opus 5 Reasoning (Max)',
    slug: 'claude-opus-5-reasoning-max',
    description:
      'Opus 5 at its deepest reasoning setting. Use for the hardest problems where thoroughness matters more than latency — SWE-bench-grade coding, advanced math, and long-horizon agents.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 74,
    title: 'Claude Opus 4.8',
    slug: 'claude-opus-4-8',
    description:
      "Anthropic's flagship model from May 2026 — stronger long-horizon agentic coding, sharper judgment, and more honest progress reporting. 1M context, 128K output, and adaptive thinking with selectable effort levels.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    // Legacy alias — kept so saved chats still resolve. Maps to High effort on the backend.
    id: 75,
    title: 'Claude Opus 4.8 Reasoning',
    slug: 'claude-opus-4-8-reasoning',
    description:
      'Reasoning-enabled variant of Claude Opus 4.8. Extended thinking for the hardest coding, math, and agentic tasks.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    hidden: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 741,
    title: 'Claude Opus 4.8 Reasoning (Low)',
    slug: 'claude-opus-4-8-reasoning-low',
    description:
      'Opus 4.8 with minimal extended thinking. Fastest and cheapest reasoning variant — good for quick answers that still benefit from a short chain of thought.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 742,
    title: 'Claude Opus 4.8 Reasoning (Medium)',
    slug: 'claude-opus-4-8-reasoning-medium',
    description:
      'Opus 4.8 with balanced extended thinking. Good depth for typical reasoning tasks without the latency of the deeper variants.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 743,
    title: 'Claude Opus 4.8 Reasoning (High)',
    slug: 'claude-opus-4-8-reasoning-high',
    description:
      "Opus 4.8's default reasoning depth. Deep extended thinking for complex coding, research, and multi-step agent work.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 744,
    title: 'Claude Opus 4.8 Reasoning (Max)',
    slug: 'claude-opus-4-8-reasoning-max',
    description:
      'Opus 4.8 at its deepest reasoning setting. Use for the hardest problems where thoroughness matters more than latency — SWE-bench-grade coding, advanced math, and long-horizon agents.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 72,
    title: 'Claude Opus 4.7',
    slug: 'claude-opus-4-7',
    description:
      "Anthropic's flagship model from April 2026. 87.6% on SWE-bench Verified and 69.4% on Terminal-Bench 2.0. 1M context, 128K output, sharper vision (3.75MP), and a new xhigh effort level.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    // Legacy alias — kept so saved chats still resolve. Maps to High effort on the backend.
    id: 73,
    title: 'Claude Opus 4.7 Reasoning',
    slug: 'claude-opus-4-7-reasoning',
    description:
      'Reasoning-enabled variant of Claude Opus 4.7. Extended thinking for the hardest coding, math, and agentic tasks.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    hidden: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 731,
    title: 'Claude Opus 4.7 Reasoning (Low)',
    slug: 'claude-opus-4-7-reasoning-low',
    description:
      'Opus 4.7 with minimal extended thinking. Fastest and cheapest reasoning variant — good for quick answers that still benefit from a short chain of thought.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 732,
    title: 'Claude Opus 4.7 Reasoning (Medium)',
    slug: 'claude-opus-4-7-reasoning-medium',
    description:
      'Opus 4.7 with balanced extended thinking. Good depth for typical reasoning tasks without the latency of the deeper variants.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 733,
    title: 'Claude Opus 4.7 Reasoning (High)',
    slug: 'claude-opus-4-7-reasoning-high',
    description:
      "Opus 4.7's default reasoning depth. Deep extended thinking for complex coding, research, and multi-step agent work.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 734,
    title: 'Claude Opus 4.7 Reasoning (Max)',
    slug: 'claude-opus-4-7-reasoning-max',
    description:
      'Opus 4.7 at its deepest reasoning setting. Use for the hardest problems where thoroughness matters more than latency — SWE-bench-grade coding, advanced math, and long-horizon agents.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 68,
    title: 'Claude Opus 4.6',
    slug: 'claude-opus-4-6',
    description:
      "Anthropic's most powerful model. 1M context window, 128K output, and adaptive thinking. State-of-the-art across coding, reasoning, and enterprise tasks.",
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    id: 69,
    title: 'Claude Opus 4.6 Reasoning',
    slug: 'claude-opus-4-6-reasoning',
    description:
      'Reasoning-enabled variant of Claude Opus 4.6. Adaptive extended thinking for the most complex challenges with maximum intelligence.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 85,
    title: 'Claude Sonnet 5.5',
    slug: 'claude-sonnet-5-5',
    description:
      "Anthropic's newest Sonnet (September 2026) — the successor to Sonnet 5 at the same price, stronger on multistep agentic coding and tool use. Adaptive thinking is always on (this variant runs it at low effort for speed). 1M context window and 128K output.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 851,
    title: 'Claude Sonnet 5.5 Reasoning (Medium)',
    slug: 'claude-sonnet-5-5-reasoning-medium',
    description:
      'Sonnet 5.5 with balanced adaptive thinking — the setting Anthropic recommends for agentic coding and multistep tool use.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 852,
    title: 'Claude Sonnet 5.5 Reasoning (High)',
    slug: 'claude-sonnet-5-5-reasoning-high',
    description:
      "Sonnet 5.5 with deep adaptive thinking (the model's own default depth) for complex coding, research, and multi-step agent work.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 853,
    title: 'Claude Sonnet 5.5 Reasoning (Max)',
    slug: 'claude-sonnet-5-5-reasoning-max',
    description:
      'Sonnet 5.5 at its deepest reasoning setting. Use for the hardest coding, math, and long-horizon agent tasks where thoroughness matters more than latency.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 78,
    title: 'Claude Sonnet 5',
    slug: 'claude-sonnet-5',
    description:
      "Anthropic's most agentic Sonnet yet (June 2026) — a drop-in upgrade to Sonnet 4.6 with the best combination of speed and intelligence, approaching Opus 4.8 quality at a lower price. 1M context window, 128K output, and adaptive thinking.",
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 79,
    title: 'Claude Sonnet 5 Reasoning',
    slug: 'claude-sonnet-5-reasoning',
    description:
      'Reasoning-enabled variant of Claude Sonnet 5 with adaptive extended thinking for advanced multi-step reasoning, complex agents, and coding tasks.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 791,
    title: 'Claude Sonnet 5 Reasoning (Max)',
    slug: 'claude-sonnet-5-reasoning-max',
    description:
      'Claude Sonnet 5 at its deepest reasoning setting. Maximum adaptive thinking effort for the hardest coding, math, and long-horizon agent tasks.',
    contextLength: 1000000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 70,
    title: 'Claude Sonnet 4.6',
    slug: 'claude-sonnet-4-6',
    description:
      'Improved coding, computer use, and long-context reasoning. Best for complex agents and knowledge work.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 71,
    title: 'Claude Sonnet 4.6 Reasoning',
    slug: 'claude-sonnet-4-6-reasoning',
    description:
      'Reasoning-enabled variant of Claude Sonnet 4.6 with extended thinking for advanced multi-step reasoning, complex agents, and coding tasks.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 62,
    title: 'Claude Sonnet 4.5 Reasoning',
    slug: 'claude-sonnet-4-5-reasoning',
    description:
      'Reasoning-enabled variant of Claude Sonnet 4.5 for advanced multi-step reasoning, complex agents, and coding tasks.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
  },
  {
    id: 5502,
    title: 'GPT-5.5 Thinking',
    slug: 'gpt-5.5-thinking',
    description:
      'Deep reasoning mode with stronger multi-step thinking, improved web research, and extended context. Built for problems that benefit from longer thinking chains.',
    contextLength: 1000000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    id: 5503,
    title: 'GPT-5.5 Pro',
    slug: 'gpt-5.5-pro',
    description:
      'Maximum performance for complex professional tasks. State-of-the-art accuracy with extra-high reasoning effort and native computer use.',
    contextLength: 1000000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    id: 5402,
    title: 'GPT-5.4 Thinking',
    slug: 'gpt-5.4-thinking',
    description:
      'Deep reasoning mode with improved web research and extended context. Maintains context for questions requiring longer thinking chains.',
    contextLength: 1000000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5403,
    title: 'GPT-5.4 Pro',
    slug: 'gpt-5.4-pro',
    description:
      'Maximum performance for complex professional tasks. Record-breaking accuracy with 33% fewer errors and native computer use capabilities.',
    contextLength: 1000000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5201,
    title: 'GPT-5.2 Instant',
    slug: 'gpt-5.2-instant',
    description:
      'Built for everyday work and learning with warmer, clearer explanations. Surfaces key information upfront with improved how-tos and technical writing.',
    contextLength: 400000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5202,
    title: 'GPT-5.2 Thinking',
    slug: 'gpt-5.2-thinking',
    description:
      'Enhanced thinking mode for complex analysis. Combines GPT-5.2 warmth with structured reasoning for research, planning, and problem-solving.',
    contextLength: 400000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5203,
    title: 'GPT-5.2 Pro',
    slug: 'gpt-5.2-pro',
    description:
      'Our smartest and most trustworthy model for difficult questions. Strongest performance in programming and best for assisting scientists.',
    contextLength: 400000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5101,
    title: 'GPT-5.1 Instant',
    slug: 'gpt-5.1-instant',
    description:
      'The snappiest GPT-5.1 experience—optimized for chatty exchanges that still keep GPT-5-level comprehension and tools.',
    contextLength: 400000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5102,
    title: 'GPT-5.1 Thinking',
    slug: 'gpt-5.1-thinking',
    description:
      'Adds deliberate reflection on top of GPT-5.1 so it can break down research, product, or strategy problems with more context awareness.',
    contextLength: 400000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5103,
    title: 'GPT-5.1 Pro',
    slug: 'gpt-5.1-pro',
    description:
      'Full-strength GPT-5.1 with the deepest reasoning chain and the most reliable long-form planning for complex agentic work.',
    contextLength: 400000,
    messageLimit: 128000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5001,
    title: 'GPT-5',
    slug: 'gpt-5',
    description: 'The best model for coding and agentic tasks across domains',
    contextLength: 200000,
    messageLimit: 100000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5002,
    title: 'GPT-5 mini',
    slug: 'gpt-5-mini',
    description:
      'A faster, more cost-efficient version of GPT-5 for well-defined tasks',
    contextLength: 100000,
    messageLimit: 50000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.25,
  },
  {
    id: 5003,
    title: 'GPT-5 nano',
    slug: 'gpt-5-nano',
    description: 'Fastest, most cost-efficient version of GPT-5.',
    contextLength: 64000,
    messageLimit: 20000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0,
  },
  {
    id: 5004,
    title: 'GPT-5 Codex',
    slug: 'gpt-5-codex',
    description: 'A version of GPT-5 optimized for coding.',
    contextLength: 400000,
    messageLimit: 200000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 5005,
    title: 'GPT-5 Chat',
    slug: 'gpt-5-chat-latest',
    description: 'Same model as used in ChatGPT.',
    contextLength: 128000,
    messageLimit: 64000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 2001,
    title: 'GPT-4.1',
    slug: 'gpt-4.1',
    description:
      "GPT 4.1 is OpenAI's flagship model for complex tasks. It is well suited for problem solving across domains.",
    contextLength: 128000,
    messageLimit: 100000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 2002,
    title: 'GPT-4.1 mini',
    slug: 'gpt-4.1-mini',
    description:
      'GPT 4.1 mini provides a balance between intelligence, speed, and cost that makes it an attractive model for many use cases.',
    contextLength: 64000,
    messageLimit: 40000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.25,
  },

  {
    id: 1001,
    title: 'GPT-4o mini',
    slug: 'gpt-4o-mini',
    description:
      'Intelligent small model from OpenAI. Optimized for fast, lightweight tasks. Ideal for quick responses and efficient processing.',
    contextLength: 32000,
    messageLimit: 25000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0,
  },
  {
    id: 1000,
    title: 'GPT-4o',
    slug: 'gpt-4o',
    description:
      'Most advanced system from OpenAI. 2x faster than GPT-4 Turbo. Additionally, GPT-4o has the best vision and performance across non-English languages of any of our models.',
    contextLength: 32000,
    messageLimit: 25000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 3,
    title: 'GPT-4',
    slug: 'gpt-4',
    description:
      'Most advanced system from OpenAI. Can solve difficult problems with greater accuracy. Has 8k context window.',
    contextLength: 8000,
    messageLimit: 5000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    hidden: true,
  },
  {
    id: 88,
    title: 'GPT-4 32K',
    slug: 'gpt-4-32k',
    description:
      'Most advanced system from OpenAI. Can solve difficult problems with greater accuracy. Has 32k context window.',
    contextLength: 32000,
    messageLimit: 25000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    hidden: true,
  },
  {
    id: 8,
    title: 'GPT-4 Turbo',
    slug: 'gpt-4-1106-preview',
    description:
      'The latest GPT-4 model with improved instruction following. Has 128k context window. Trained up to April 2023 data.',
    contextLength: 128000,
    messageLimit: 25000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    hidden: true,
  },
  {
    id: 88,
    title: 'GPT-4 Turbo',
    slug: 'gpt-4-turbo-preview',
    description:
      'The latest GPT-4 model with improved instruction following. Has 128k context window. Trained up to April 2023 data.',
    contextLength: 128000,
    messageLimit: 25000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    hidden: true,
  },
  {
    id: 892,
    title: 'o3',
    slug: 'o3',
    description:
      'A well-rounded and powerful model across domains. It sets a new standard for math, science, coding, and visual reasoning tasks. It also excels at technical writing and instruction-following. Use it to think through multi-step problems that involve analysis across text, code, and images.',
    contextLength: 200000,
    messageLimit: 50000,
    icon: OpenAISvg,
    provider: 'OpenAI',
    availableForFree: false,
    hidden: false,
    features: [
      'reasoning',
      'python',
      'internet',
      'generate images',
      'edit images',
    ],
    multiplier: 1,
  },
  {
    id: 53,
    title: 'Claude 3 Haiku',
    slug: 'claude-3-haiku-20240307',
    description:
      'Fastest and most compact model for near-instant responsiveness. Quick and accurate targeted performance.',
    contextLength: 200000,
    // Served by Claude Haiku 5.5 (chatModelRouting.ts), which costs 5x
    // more above a 100K-token prompt; keep history well under that.
    messageLimit: 60000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: true,
    countWords: false,
    hidden: true,
  },
  {
    id: 66,
    title: 'Claude Opus 4.5 Reasoning',
    slug: 'claude-opus-4-5-reasoning',
    description:
      'Reasoning-enabled variant of Claude Opus 4.5. Maximum intelligence with extended thinking for the most complex challenges.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
  },
  {
    id: 54,
    title: 'Claude 3.5 Haiku',
    slug: 'claude-3-5-haiku-20241022',
    description:
      'Next generation of Anthropic fastest model. Surpasses Claude 3 Opus on many intelligence benchmarks.',
    contextLength: 200000,
    // Served by Claude Haiku 5.5 (chatModelRouting.ts), which costs 5x
    // more above a 100K-token prompt; keep history well under that.
    messageLimit: 60000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: true,
    countWords: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.25,
    hidden: true,
  },
  // Claude Haiku 5.5 is priced by prompt length: $0.10/$0.50 per 1M up to a
  // 100K-token prompt, $0.50/$2.50 above it. messageLimit is kept well under
  // 100K so the history plus tool definitions stays on the cheaper rate card.
  {
    id: 86,
    title: 'Claude Haiku 5.5',
    slug: 'claude-haiku-5-5',
    description:
      "Anthropic's newest Haiku (October 2026) — the fastest and lowest-cost Claude, and its most capable small model, at about a quarter of Haiku 4.5's cost. Adaptive thinking is always on (this variant runs it at low effort for speed). 1M context window and 128K output.",
    contextLength: 1000000,
    messageLimit: 60000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.1,
  },
  {
    id: 861,
    title: 'Claude Haiku 5.5 Reasoning (Medium)',
    slug: 'claude-haiku-5-5-reasoning-medium',
    description:
      "Haiku 5.5 with balanced adaptive thinking — the model's own default depth and Anthropic's recommended starting point, including for agentic coding.",
    contextLength: 1000000,
    messageLimit: 60000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.1,
  },
  {
    id: 862,
    title: 'Claude Haiku 5.5 Reasoning (High)',
    slug: 'claude-haiku-5-5-reasoning-high',
    description:
      'Haiku 5.5 with deep adaptive thinking for knowledge work, longer agent tasks, and strict instruction following.',
    contextLength: 1000000,
    messageLimit: 60000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.1,
  },
  {
    id: 863,
    title: 'Claude Haiku 5.5 Reasoning (Max)',
    slug: 'claude-haiku-5-5-reasoning-max',
    description:
      'Haiku 5.5 at its deepest reasoning setting. Replies take longer and use more tokens — for hard problems where a small, low-cost model still needs to think thoroughly.',
    contextLength: 1000000,
    messageLimit: 60000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.1,
  },
  {
    id: 51,
    title: 'Claude 3 Sonnet',
    slug: 'claude-3-sonnet-20240229',
    description:
      'Ideal balance of intelligence and speed for enterprise workloads. Has 200k context window. Trained till August 2023 data.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    hidden: true,
  },
  {
    id: 511,
    title: 'Claude 3.5 Sonnet',
    slug: 'claude-3-5-sonnet-20241022',
    description:
      'Most intelligent model from Anthropic. Has 200k context window. Trained till April 2024 data.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
    hidden: true,
  },
  {
    id: 5114,
    title: 'Claude 3.7 Sonnet',
    slug: 'claude-3-7-sonnet-latest',
    description:
      "Claude 3.7 Sonnet is the first hybrid reasoning model and Anthropic's most intelligent model to date.",
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
    hidden: true,
  },
  {
    id: 5111,
    title: 'Claude 3.7 Sonnet Reasoning',
    slug: 'claude-3-7-sonnet-latest-reasoning',
    description:
      "Claude 3.7 Sonnet is the first hybrid reasoning model and Anthropic's most intelligent model to date.",
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
    hidden: true,
  },
  {
    id: 52,
    title: 'Claude 3 Opus',
    slug: 'claude-3-opus-20240229',
    description:
      'Most powerful model for highly complex tasks. Top-level performance, intelligence, fluency, and understanding. Has 200k context window. Trained till August 2023 data.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    multiplier: 1,
    hidden: true,
  },
  {
    id: 56,
    title: 'Claude Sonnet 4',
    slug: 'claude-sonnet-4-0',
    description:
      "Anthropic's balanced model for enterprise workloads, representing the next generation of Claude Sonnet.",
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
    hidden: true,
  },
  {
    id: 58,
    title: 'Claude Sonnet 4 Reasoning',
    slug: 'claude-sonnet-4-0-reasoning',
    description:
      "Anthropic's balanced reasoning model for enterprise workloads, representing the next generation of Claude Sonnet.",
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.5,
    hidden: true,
  },
  {
    id: 61,
    title: 'Claude Sonnet 4.5',
    slug: 'claude-sonnet-4-5',
    description:
      'Best model for complex agents and coding, with the highest intelligence across most tasks.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 55,
    title: 'Claude Opus 4',
    slug: 'claude-opus-4-0',
    description:
      "Anthropic's most powerful model for highly complex tasks, representing the next generation of Claude Opus.",
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
    hidden: true,
  },

  {
    id: 57,
    title: 'Claude Opus 4 Reasoning',
    slug: 'claude-opus-4-0-reasoning',
    description:
      "Anthropic's most powerful reasoning model for highly complex tasks, representing the next generation of Claude Opus.",
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
    hidden: true,
  },
  {
    id: 59,
    title: 'Claude Opus 4.1',
    slug: 'claude-opus-4-1-20250805',
    description:
      'An upgrade to Claude Opus 4 on agentic tasks, real-world coding, and reasoning. Advanced state-of-the-art model.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
    hidden: true,
  },
  {
    id: 60,
    title: 'Claude Opus 4.1 Reasoning',
    slug: 'claude-opus-4-1-20250805-reasoning',
    description:
      'Reasoning-enabled variant of Claude Opus 4.1 for advanced multi-step reasoning and agentic tasks.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 1,
    hidden: true,
  },
  {
    id: 65,
    title: 'Claude Opus 4.5',
    slug: 'claude-opus-4-5',
    description:
      'Premium model combining maximum intelligence with practical performance. State-of-the-art across all domains with superior vision, reasoning, and mathematics.',
    contextLength: 200000,
    messageLimit: 150000,
    icon: AnthropicSvg,
    provider: 'Anthropic',
    availableForFree: false,
    isNew: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
  },
  {
    id: 68,
    title: 'Mistral Large',
    slug: 'mistral-large',
    description:
      "Mistral's most advanced Large Language Model (LLM). Best-in-class performance in French, German, Spanish, and Italian - in addition to English and others.",
    contextLength: 32000,
    messageLimit: 5000,
    icon: MistralAISvg,
    provider: 'Mistral AI',
    availableForFree: false,
    hidden: true,
  },
  {
    id: 2360,
    title: 'Muse Spark 1.3',
    slug: 'muse-spark-1.3',
    description:
      "Meta's latest Muse Spark model, tuned for agentic workflows — multi-step tool, browser and long-horizon tasks — with stronger coding than 1.2. Always-on reasoning, native image, video and PDF understanding, and a 1M-token context window.",
    contextLength: 1048576,
    messageLimit: 131072,
    icon: MetaSvg,
    provider: 'Meta',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 0.25,
  },
  {
    id: 2361,
    title: 'Muse Spark 1.3 (Max)',
    slug: 'muse-spark-1.3-max',
    description:
      'Muse Spark 1.3 at its deepest reasoning budget (extended reasoning). Use for the hardest coding and long-horizon agentic problems where thoroughness matters more than latency.',
    contextLength: 1048576,
    messageLimit: 131072,
    icon: MetaSvg,
    provider: 'Meta',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 0.25,
  },
  {
    id: 2362,
    title: 'Muse Wonder 1.0',
    slug: 'muse-wonder-1.0',
    description:
      "Meta's most capable model (preview), built for complex software engineering and long-horizon agentic work: multi-file coding, long multi-step tasks and sub-agent orchestration. Always-on reasoning, native image, video, audio and PDF understanding, and a 1M-token context window.",
    contextLength: 1048576,
    messageLimit: 131072,
    icon: MetaSvg,
    provider: 'Meta',
    availableForFree: false,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 1,
  },
  {
    id: 2363,
    title: 'Muse Wonder 1.0 (Max)',
    slug: 'muse-wonder-1.0-max',
    description:
      'Muse Wonder 1.0 at its deepest reasoning budget (extended reasoning). Use for the hardest coding and long-horizon agent tasks where thoroughness matters more than speed and cost.',
    contextLength: 1048576,
    messageLimit: 131072,
    icon: MetaSvg,
    provider: 'Meta',
    availableForFree: false,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 1,
  },
  {
    id: 23,
    title: 'GLM-4.6',
    slug: 'glm-4p6',
    description:
      'Version 4.6 of the GLM series with comprehensive enhancements across multiple domains, including real-world coding, long-context processing, reasoning, searching, writing, and agentic applications.',
    contextLength: 198000,
    messageLimit: 100000,
    icon: ZAISvg,
    provider: 'Z.ai',
    availableForFree: true,
    isNew: false,
    hidden: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.25,
  },
  {
    id: 233,
    title: 'GLM-4.7',
    slug: 'glm-4p7',
    description:
      'Latest iteration in the GLM series with comprehensive enhancements across multiple domains, including real-world coding, long-context processing, reasoning, searching, writing, and agentic applications.',
    contextLength: 198000,
    messageLimit: 100000,
    icon: ZAISvg,
    provider: 'Z.ai',
    availableForFree: true,
    isNew: false,
    hidden: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.25,
  },
  {
    id: 11,
    title: 'Gemini 1.0 Pro',
    slug: 'google-gemini-pro',
    description:
      "Google's largest and most capable AI model. Last updated in December 2023. Has 30k context window.",
    contextLength: 30720,
    messageLimit: 10000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: false,
    hidden: true,
  },
  {
    id: 1332,
    title: 'Gemini 1.5 Pro',
    slug: 'gemini-1.5-pro-latest',
    description:
      "Google's largest and most capable AI model. Last updated in April 2024. Has 1m context window.",
    contextLength: 200000,
    messageLimit: 150000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 1,
    hidden: true,
  },
  {
    id: 11,
    title: 'Gemini Flash 1.5',
    slug: 'gemini-1.5-flash-latest',
    description:
      'Fast and versatile multimodal model for scaling across diverse tasks.',
    contextLength: 30720,
    messageLimit: 10000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: false,
    hidden: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0,
  },
  {
    id: 11,
    title: 'Gemini Flash 2.5',
    slug: 'gemini-2.5-flash',
    description:
      'Fast and versatile multimodal model for scaling across diverse tasks.',
    contextLength: 128000,
    messageLimit: 50000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0,
  },
  {
    id: 111,
    title: 'Gemini 2.5 Pro',
    slug: 'gemini-2.5-pro-exp-03-25',
    description:
      'Google state-of-the-art thinking model, capable of reasoning over complex problems in code, math, and STEM, as well as analyzing large datasets, codebases, and documents using long context.',
    contextLength: 128000,
    messageLimit: 100000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  // gemini-3-pro-preview — hidden, routed to 3.1 in new-chat API
  {
    id: 1111,
    title: 'Gemini 3 Pro',
    slug: 'gemini-3-pro-preview',
    description: 'Google state-of-the-art new generation thinking model.',
    contextLength: 1000000,
    messageLimit: 100000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: true,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
    hidden: true,
  },
  // Gemini 4 Argon (GA 2026-10-13): Google's flagship, on Vertex `global`.
  // 1M context, 65,536 output tokens per call, thinking levels low/medium/high
  // (the route sends high unless the picker sends low or medium). Paid-only,
  // like at Google. Billing: @/constants/gemini4Argon (0.5x while Google's
  // $2/$10 introductory price lasts, 1x from 2026-12-01 when it becomes $4/$20).
  {
    id: 11120,
    title: 'Gemini 4 Argon',
    slug: 'gemini-4-argon',
    description:
      "Google's most capable model (October 2026), built for deep reasoning, complex coding and long-horizon agentic work. Thinking model with a 1M-token context window and native image, audio, video and PDF understanding.",
    contextLength: 1048576,
    messageLimit: 100000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: false,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: gemini4ArgonMultiplier(),
  },
  // gemini-3.1-pro-preview
  {
    id: 11110,
    title: 'Gemini 3.1 Pro',
    slug: 'gemini-3.1-pro-preview',
    description:
      "Google's next-generation model built for the best quality on complex, agentic workloads.",
    contextLength: 1000000,
    messageLimit: 100000,
    icon: GoogleSvg,
    provider: 'Google',
    availableForFree: true,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 12,
    title: 'Grok',
    slug: 'grok-beta',
    description:
      "xAI's witty AI assistant inspired by The Hitchhiker's Guide to the Galaxy. Excels at answering questions with humor and suggesting insightful questions to ask.",
    contextLength: 25000,
    messageLimit: 20000,
    icon: XAISvg,
    provider: 'xAI',
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    hidden: true,
    multiplier: 0.5,
  },
  {
    id: 12,
    title: 'Grok 2',
    slug: 'grok-2-latest',
    description:
      "xAI's witty AI assistant inspired by The Hitchhiker's Guide to the Galaxy. Excels at answering questions with humor and suggesting insightful questions to ask.",
    contextLength: 25000,
    messageLimit: 20000,
    icon: XAISvg,
    provider: 'xAI',
    hidden: true,
    availableForFree: false,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 15,
    title: 'Grok 3 Mini',
    slug: 'grok-3-mini',
    description:
      "xAI's lightweight model that thinks before responding. Great for simple or logic-based tasks that do not require deep domain knowledge.",
    contextLength: 25000,
    messageLimit: 20000,
    icon: XAISvg,
    provider: 'xAI',
    availableForFree: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 0,
    hidden: true,
  },
  {
    id: 17,
    title: 'Grok 4',
    slug: 'grok-4',
    description:
      "xAI's latest and greatest flagship model, offering unparalleled performance in natural language, math and reasoning - the perfect jack of all trades.",
    contextLength: 256000,
    messageLimit: 200000,
    icon: XAISvg,
    provider: 'xAI',
    availableForFree: true,
    isNew: true,
    hidden: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.5,
  },
  {
    id: 29,
    title: 'Grok 4.20',
    slug: 'grok-4.20-0309-non-reasoning',
    description:
      "xAI's newest flagship model with industry-leading speed and agentic tool calling. Combines the lowest hallucination rate on the market with strict prompt adherence for consistently precise, truthful responses.",
    contextLength: 256000,
    messageLimit: 200000,
    icon: XAISvg,
    provider: 'xAI',
    availableForFree: false,
    isNew: true,
    features: ['internet', 'generate images', 'edit images', 'python'],
    multiplier: 0.25,
  },
  {
    id: 30,
    title: 'Grok 4.20 Reasoning',
    slug: 'grok-4.20-0309-reasoning',
    description:
      "xAI's newest flagship model with industry-leading speed and agentic tool calling. Reasoning-enabled variant with the lowest hallucination rate on the market and strict prompt adherence.",
    contextLength: 256000,
    messageLimit: 200000,
    icon: XAISvg,
    provider: 'xAI',
    availableForFree: false,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 0.25,
  },
  {
    id: 32,
    title: 'Grok 4.5',
    slug: 'grok-4.5',
    description:
      "xAI's latest flagship model with a 500K token context window, native vision, and configurable reasoning effort. Strong agentic tool calling at a lower price than previous flagships.",
    contextLength: 500000,
    messageLimit: 400000,
    icon: XAISvg,
    provider: 'xAI',
    availableForFree: false,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.25,
  },
  // Grok 4.7 (2026-09-21): same $2 / $0.50 cached / $6 per 1M as Grok 4.6
  // (doubling above 200K prompt tokens), larger base model, longer reasoning.
  // Reasoning is always on; we send no effort, so xAI's default `high`.
  {
    id: 34,
    title: 'Grok 4.7',
    slug: 'grok-4.7',
    description:
      "xAI's newest frontier model for coding, agentic tasks, and knowledge work: a larger base model with longer reasoning than Grok 4.6, at the same price. 500K token context window and native vision.",
    contextLength: 500000,
    messageLimit: 400000,
    icon: XAISvg,
    provider: 'xAI',
    availableForFree: false,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.25,
  },
  {
    id: 33,
    title: 'Grok 4.6',
    slug: 'grok-4.6',
    description:
      "xAI's frontier model built for coding, agentic tasks, and knowledge work. Standout agentic performance with a 500K token context window, native vision, and configurable reasoning effort — at the same price as Grok 4.5.",
    contextLength: 500000,
    messageLimit: 400000,
    icon: XAISvg,
    provider: 'xAI',
    // Paid plans only since 2026-09-13 (Dmytro's call). It was free — and the
    // default for every visitor — from 2026-09-10 to 2026-09-13, so many Free
    // users still carry it in localStorage; on the web that is deliberate:
    // their next send hits the route's 401 and Chat.tsx opens the pricing
    // modal. The mobile app has no purchase UI, so it falls back silently.
    availableForFree: false,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.25,
  },
  {
    id: 31,
    title: 'Grok 4.3',
    slug: 'grok-4.3',
    description:
      "xAI's newest flagship with a 1M token context window. Leads in agentic tool calling, instruction following, and non-hallucination rate, with configurable reasoning effort.",
    contextLength: 1000000,
    messageLimit: 800000,
    icon: XAISvg,
    provider: 'xAI',
    availableForFree: false,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
    ],
    multiplier: 0.25,
  },
  {
    id: 21,
    title: 'Perplexity Sonar Pro',
    slug: 'sonar-pro',
    description:
      'Advanced online LLM with real-time web search capabilities. Provides accurate, up-to-date information with source citations.',
    contextLength: 200000,
    messageLimit: 100000,
    icon: PerplexitySvg,
    provider: 'Perplexity',
    availableForFree: false,
    features: ['internet'],
    multiplier: 0.5,
  },
  {
    id: 22,
    title: 'Perplexity Sonar',
    slug: 'sonar',
    description:
      'Online LLM with web search capabilities. Provides current information with source citations at an efficient cost.',
    contextLength: 200000,
    messageLimit: 100000,
    icon: PerplexitySvg,
    provider: 'Perplexity',
    availableForFree: true,
    features: ['internet'],
    multiplier: 0.25,
  },
  {
    id: 133,
    title: 'Deepseek V4 Pro',
    slug: 'deepseek-v4-pro',
    description:
      'Flagship open-source Mixture-of-Experts model designed for frontier reasoning, advanced coding, and long-context intelligence at scale (up to 1M tokens).',
    contextLength: 1000000,
    messageLimit: 131072,
    icon: DeepseekSvg,
    provider: 'Deepseek',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.25,
  },

  {
    id: 135,
    title: 'Deepseek V4.1 Flash',
    slug: 'deepseek-v4.1-flash',
    description:
      'Newest Flash-tier model from Deepseek (552B Mixture-of-Experts, 8B/16B active parameters) with native image understanding and a 1M-token context window. Benchmarks ahead of Deepseek V4 Pro at Flash-tier speed and cost.',
    contextLength: 1000000,
    messageLimit: 131072,
    icon: DeepseekSvg,
    provider: 'Deepseek',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.05,
  },

  {
    id: 1351,
    title: 'Deepseek V4.1 Flash (Max)',
    slug: 'deepseek-v4.1-flash-max',
    description:
      'Deepseek V4.1 Flash at its deepest thinking budget. Slower to answer and spends more words on reasoning — use it when a task needs more thoroughness than the standard Flash setting gives.',
    contextLength: 1000000,
    messageLimit: 131072,
    icon: DeepseekSvg,
    provider: 'Deepseek',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'reasoning',
    ],
    multiplier: 0.05,
  },

  {
    id: 1449,
    title: 'Kimi K3',
    slug: 'kimi-k3',
    description:
      "Moonshot's flagship open model with a 1M-token context window. Always-on step-by-step reasoning, native multimodal input, and strong long-horizon agentic tool use.",
    contextLength: 1000000,
    // Deliberately well under the 1M window: messageLimit is the per-call
    // prompt cap (ensureTokenLimit) and is re-sent every agentic step, so it
    // is a cost bound, not a capability bound. 2x Kimi K2.6; raise further
    // only with pricing in mind (K3 input ≈ $3/1M — an 800K prompt would be
    // ~$2.40 per step). Compare: Gemini 3.5 Flash 1M→100K, Grok 4.3
    // (premium-gated) 1M→800K.
    messageLimit: 256000,
    icon: MoonshotSvg,
    provider: 'Moonshot',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.5,
  },
  {
    id: 1448,
    title: 'Kimi K2.7 Code',
    slug: 'kimi-k2.7-code',
    description:
      'Coding-focused agentic model built upon Kimi K2.6. Strengthens end-to-end completion across complex software engineering workflows on real-world long-horizon coding tasks, while cutting thinking-token usage by ~30%.',
    contextLength: 256000,
    messageLimit: 128000,
    icon: MoonshotSvg,
    provider: 'Moonshot',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.1,
  },
  {
    id: 1447,
    title: 'Kimi K2.6',
    slug: 'kimi-k2.6',
    description:
      'Open-source, native multimodal agentic model. Advances long-horizon coding, coding-driven design, proactive autonomous execution, and swarm-based task orchestration.',
    contextLength: 256000,
    messageLimit: 128000,
    icon: MoonshotSvg,
    provider: 'Moonshot',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.1,
  },
  {
    id: 2349,
    title: 'MiniMax M3',
    slug: 'minimax-m3',
    description:
      'Native multimodal MoE model (~428B total / ~23B active parameters) with a 512K context window. Fuses text, image, and video, and introduces MiniMax Sparse Attention (MSA) for efficient long-context, frontier-level agentic coding and cowork.',
    contextLength: 512000,
    messageLimit: 64000,
    icon: MiniMaxSvg,
    provider: 'MiniMax',
    availableForFree: true,
    isNew: true,
    features: [
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.1,
  },
  {
    id: 2364,
    title: 'MiniMax M3.1 Flash',
    slug: 'minimax-m3.1-flash',
    description:
      'Fast, low-cost MiniMax model with frontier-level agentic work, coding, and visual reasoning at a third of the cost of M3. Always thinks, with a 1M context window and text, image, and video input.',
    contextLength: 1000000,
    messageLimit: 128000,
    icon: MiniMaxSvg,
    provider: 'MiniMax',
    availableForFree: true,
    isNew: true,
    features: [
      'reasoning',
      'internet',
      'generate images',
      'edit images',
      'python',
      'vision',
    ],
    multiplier: 0.05,
  },
] as const;

// Default model for the Marky Agent chat when the user has no saved
// preference (new users / cleared localStorage). Existing explicit model
// selections remain untouched.
export const DEFAULT_CHAT_MODEL_SLUG = 'glm-5p3-flash';
/**
 * Chat model slugs that were removed from the catalogue but still live in
 * `chats.chat_model`, `ai_employees.chat_model`, scheduled tasks and API
 * callers' requests. They resolve to a live slug BEFORE the server's
 * "is this slug in chatModels" validation (which 400s on unknown slugs) and
 * on client-side restore, so old conversations keep working. Keep entries
 * forever — removing one breaks every chat that still carries the slug.
 *
 * DeepSeek V3.1 / V3.2 / R1 / R1-0528 / R1-Zero: retired 2026-09-08 when
 * DeepSeek's platform (their only host after Fireworks was dropped) stopped
 * serving anything but V4. DeepSeek V4 Flash: retired 2026-09-13 after DeepSeek
 * replaced it with V4.1 Flash (released 2026-09-10 under the versionless API
 * id `deepseek-flash`) and kept `deepseek-v4-flash` only as a temporary alias.
 * DeepSeek's own `deepseek-chat` / `deepseek-reasoner` names answer as V4.1
 * Flash, so that is where all of these land.
 */
export const LEGACY_CHAT_MODEL_ALIASES: Record<string, ChatModelType> = {
  'deepseek-v3': 'deepseek-v4.1-flash',
  'deepseek-v3p2': 'deepseek-v4.1-flash',
  'deepseek-r1': 'deepseek-v4.1-flash',
  'deepseek-r1-0528': 'deepseek-v4.1-flash',
  'deepseek-ai/DeepSeek-R1-Zero': 'deepseek-v4.1-flash',
  'deepseek-v4-flash': 'deepseek-v4.1-flash',
  // Meta Llama 2 / 3 / 3.3 / 3.1 405B / 4 Maverick (Fireworks-hosted): retired
  // 2026-09-08 in favour of Muse Spark 1.3 on Meta's own Model API.
  'meta-llama-2-70b': 'muse-spark-1.3',
  'meta-llama-3-70b': 'muse-spark-1.3',
  'meta-llama-3.3-70b': 'muse-spark-1.3',
  'meta-llama-3-405b': 'muse-spark-1.3',
  'llama4-maverick-instruct-basic': 'muse-spark-1.3',
  // MiniMax M2.1 / M2.5 / M2.7: retired 2026-09-08 when the family moved to
  // MiniMax's own platform — only M3 is offered.
  'minimax-m2': 'minimax-m3',
  'minimax-m2p5': 'minimax-m3',
  'minimax-m2p7': 'minimax-m3',
  // Kimi K2 / K2 Thinking / K2.5: retired 2026-09-08 when the Kimi family
  // moved to Moonshot's own API, which hosts only K2.6 / K2.7 Code / K3.
  'kimi-k2-instruct': 'kimi-k2.6',
  'kimi-k2-thinking': 'kimi-k2.6',
  'kimi-k2.5': 'kimi-k2.6',
  // Last Fireworks-hosted models: retired 2026-09-17 when Fireworks was
  // dropped entirely (account suspended for billing, every call errored).
  // Each maps to a first-party model in the same or a cheaper price tier.
  'mixtral-8x7b': 'gpt-4o-mini',
  'gept-oss-120b': 'gpt-5-nano',
  'qwen3p8-max': 'glm-5p3',
  'qwen3p7-plus': 'glm-5p3',
  'qwen3p6-plus': 'glm-5p3',
  // The Qwen3 Coder / 235B aliases were served by Kimi K2 and DeepSeek V3.2
  // checkpoints on Fireworks.
  'qwen3-coder': 'minimax-m3',
  'qwen3-235b-instruct': 'minimax-m3',
  'qwen3-235b-thinking': 'deepseek-v4.1-flash',
  // GLM-5 / 5.1 / 5.2: retired 2026-09-19 in favour of GLM 5.3 (same 0.25x
  // tier, same Z.ai account). The slugs stay in the billing lists because
  // historical rows carry them — chat rows, and preset rows written by the
  // old GLM 5.2 last-resort fallback (now GLM 5.3, see generate.ts).
  'glm-5': 'glm-5p3',
  'glm-5p1': 'glm-5p3',
  'glm-5p2': 'glm-5p3',
  // OpenAI shuts these down in the API on 2026-10-23. Each maps to a model in
  // the same or a cheaper price tier: GPT-3.5 Turbo and GPT-4.1 nano were
  // free (0x) like their successors, GPT-4 Turbo was 0.75x, o3-mini 0.5x.
  // o1 / o1-mini were hidden already.
  'gpt-3.5-turbo-0125': 'gpt-4o-mini',
  'gpt-4.1-nano': 'gpt-5-nano',
  'gpt-4-turbo': 'gpt-4.1',
  o1: 'gpt-5',
  'o1-mini': 'gpt-5-mini',
  'o3-mini': 'gpt-5-mini',
  'o4-mini': 'gpt-5-mini',
  // Gemini 3.5 / 3.6 / 3.7 Flash: retired 2026-10-10 after Google deprecated
  // 3.7 Flash and began redirecting it to 3.8 Flash (same promotional price).
  // Same 0.25x tier; the slugs stay in the billing lists for historical rows.
  'gemini-3.5-flash': 'gemini-3.8-flash',
  'gemini-3.6-flash': 'gemini-3.8-flash',
  'gemini-3.7-flash': 'gemini-3.8-flash',
  // Claude Haiku 4.5: retired 2026-10-13 in favour of Claude Haiku 5.5, which
  // is cheaper for us ($0.10/$0.50 vs $1/$5 per 1M) and for users (0.1x vs
  // 0.25x). The reasoning card used a 10K thinking budget, closest to Haiku
  // 5.5's medium effort (its API default). The slugs stay in the billing
  // lists for historical rows.
  'claude-haiku-4-5': 'claude-haiku-5-5',
  'claude-haiku-4-5-reasoning': 'claude-haiku-5-5-reasoning-medium',
};

/** Live slug for a possibly-retired chat model slug (identity when current). */
export function resolveChatModelSlug<T extends string | null | undefined>(
  slug: T,
): T | ChatModelType {
  if (!slug) return slug;
  return LEGACY_CHAT_MODEL_ALIASES[slug] ?? slug;
}

export const DEFAULT_CHAT_MODEL: ChatModel =
  chatModels.find((m) => m.slug === DEFAULT_CHAT_MODEL_SLUG) ?? chatModels[0];

export type ChatModelProvider = NonNullable<ChatModel['provider']>;

export interface FeaturedChatModel {
  slug: ChatModelType;
  /** Short English line under the name in the picker (a `chat` i18n key). */
  tagline: string;
  /**
   * Shows a "New" badge on the row. Set it only for a recent launch and drop
   * it after a few weeks: the catalogue's own `isNew` is on almost every
   * current model, so it can't tell users what actually just shipped.
   */
  isNew?: boolean;
}

/**
 * The model picker shows only these models per provider by default, in this
 * order; every other visible model of the provider sits behind "See more".
 * List BASE slugs only: a model's reasoning variants (`-max`,
 * `-reasoning-*`) are offered as modes on its row. When a newer model ships,
 * swap it in here — a provider missing from this map shows its first two
 * models instead (see @/lib/chatModelPicker).
 */
export const FEATURED_CHAT_MODELS: Partial<
  Record<ChatModelProvider, readonly FeaturedChatModel[]>
> = {
  OpenAI: [
    { slug: 'gpt-6-astra', tagline: 'Most capable, for the hardest work' },
    {
      slug: 'gpt-6.1-sol',
      tagline: 'Smart and fast at a lower cost',
      isNew: true,
    },
    { slug: 'gpt-6-luna', tagline: 'Fastest and most affordable' },
  ],
  Anthropic: [
    {
      slug: 'claude-opus-5-5',
      tagline: 'Deep reasoning for complex work',
      isNew: true,
    },
    { slug: 'claude-fable-5-1', tagline: "Anthropic's most capable model" },
    {
      slug: 'claude-sonnet-5-5',
      tagline: 'Great for coding and agents',
      isNew: true,
    },
    {
      slug: 'claude-haiku-5-5',
      tagline: 'Fast and lightweight',
      isNew: true,
    },
  ],
  Google: [
    {
      slug: 'gemini-4-argon',
      tagline: 'Best quality for complex tasks',
      isNew: true,
    },
    { slug: 'gemini-3.8-flash', tagline: 'Fast, capable all-rounder' },
  ],
  'Z.ai': [
    { slug: 'glm-5p3-flash', tagline: 'Fast and low cost, understands images' },
    { slug: 'glm-5p3', tagline: 'Flagship for coding and agents' },
  ],
  xAI: [
    {
      slug: 'grok-4.7',
      tagline: 'Frontier coding and agentic work',
      isNew: true,
    },
  ],
  Deepseek: [
    { slug: 'deepseek-v4.1-flash', tagline: 'Fast and very low cost' },
    { slug: 'deepseek-v4-pro', tagline: 'Open model for deep reasoning' },
  ],
  Meta: [
    {
      slug: 'muse-wonder-1.0',
      tagline: 'Flagship for coding and agents',
      isNew: true,
    },
    { slug: 'muse-spark-1.3', tagline: 'Tuned for multi-step agentic work' },
  ],
  Moonshot: [{ slug: 'kimi-k3', tagline: 'Open flagship with long context' }],
  MiniMax: [
    {
      slug: 'minimax-m3.1-flash',
      tagline: 'Fast and low cost agentic model',
      isNew: true,
    },
    { slug: 'minimax-m3', tagline: 'Multimodal agentic coding' },
  ],
  Perplexity: [
    { slug: 'sonar-pro', tagline: 'Answers with live web citations' },
    { slug: 'sonar', tagline: 'Quick web answers with sources' },
  ],
};

/** Provider order in the model picker; providers not listed come last. */
export const CHAT_MODEL_PROVIDER_ORDER: readonly ChatModelProvider[] = [
  'OpenAI',
  'Anthropic',
  'Google',
  'Z.ai',
  'xAI',
  'Deepseek',
  'Meta',
  'Moonshot',
  'MiniMax',
  'Perplexity',
  'Mistral AI',
  'Qwen',
];

/** Reasoning effort a chat model slug runs at, as shown in the picker. */
export type ChatModelEffort =
  | 'off'
  | 'on'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | 'max';

/** Efforts a client may request through the chat request's `reasoningEffort`. */
export type ChatRequestEffort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export interface ChatModelEffortInfo {
  /** What this slug runs at without an override. */
  effort: ChatModelEffort;
  /**
   * Other efforts the chat route accepts for this slug via the request's
   * `reasoningEffort` field (provider-side effort, same model and price).
   */
  options?: readonly ChatRequestEffort[];
}

const GPT_6_EFFORTS: ChatModelEffortInfo = {
  effort: 'medium',
  options: ['low', 'high', 'xhigh'],
};
const GPT_56_EFFORTS: ChatModelEffortInfo = {
  effort: 'medium',
  options: ['low', 'high'],
};
const THINKING_OFF: ChatModelEffortInfo = { effort: 'off' };

/**
 * Effort per slug for the picker's Effort control. Slugs ending in
 * `-reasoning-<level>` or `-max` take their effort from the suffix and only
 * need an entry when that is wrong; a plain `-reasoning` suffix reads as
 * "On" (budget-based thinking). `options` must only list levels the slug's
 * provider accepts: the chat route applies them as the provider's effort
 * (OpenAI `reasoningEffort`, Z.ai/DeepSeek/Meta `reasoning_effort`). Anthropic
 * effort stays slug-based (`-reasoning-<level>` cards), so no options there.
 */
export const CHAT_MODEL_EFFORTS: Partial<
  Record<ChatModelType, ChatModelEffortInfo>
> = {
  'gpt-6-astra': GPT_6_EFFORTS,
  'gpt-6.1-sol': GPT_6_EFFORTS,
  'gpt-6-sol': GPT_6_EFFORTS,
  'gpt-6-luna': GPT_6_EFFORTS,
  'gpt-5.6-sol': GPT_56_EFFORTS,
  'gpt-5.6-terra': GPT_56_EFFORTS,
  'gpt-5.6-luna': GPT_56_EFFORTS,
  // Adaptive thinking is always on; the base slugs run it at these levels.
  'claude-opus-5-5': { effort: 'low' },
  'claude-sonnet-5-5': { effort: 'low' },
  'claude-haiku-5-5': { effort: 'low' },
  'claude-fable-5-1': { effort: 'high' },
  'claude-fable-5': { effort: 'high' },
  'claude-sonnet-5-reasoning': { effort: 'high' },
  // Base slugs that send no thinking (or disable it explicitly).
  'claude-opus-5': THINKING_OFF,
  'claude-opus-4-8': THINKING_OFF,
  'claude-opus-4-7': THINKING_OFF,
  'claude-opus-4-6': THINKING_OFF,
  'claude-opus-4-5': THINKING_OFF,
  'claude-sonnet-5': THINKING_OFF,
  'claude-sonnet-4-6': THINKING_OFF,
  'claude-sonnet-4-5': THINKING_OFF,
  'glm-5p3': { effort: 'high', options: ['low'] },
  'glm-5p3-flash': { effort: 'high', options: ['low'] },
  'deepseek-v4.1-flash': { effort: 'high', options: ['low', 'medium'] },
  'deepseek-v4-pro': { effort: 'xhigh', options: ['high', 'max'] },
  // MiniMax default is max; the route pins high (getMiniMaxReasoningEffort).
  'minimax-m3.1-flash': { effort: 'high', options: ['low', 'medium', 'max'] },
  'muse-spark-1.3': { effort: 'medium', options: ['low', 'high'] },
  'muse-wonder-1.0': { effort: 'medium', options: ['low', 'high', 'xhigh'] },
  // xAI default is high; every level accepted on Chat Completions with
  // @ai-sdk/xai 3.0.139+ (probed 2026-10-02).
  'grok-4.7': { effort: 'high', options: ['low', 'medium', 'xhigh'] },
  'grok-4.6': { effort: 'high', options: ['low', 'medium', 'xhigh'] },
  // Vertex thinkingLevel; the route runs high by default.
  'gemini-3.8-flash': { effort: 'high', options: ['low', 'medium'] },
  'gemini-3.1-pro-preview': { effort: 'high', options: ['low', 'medium'] },
  // Gemini 4 Argon takes the same three levels (API default medium).
  'gemini-4-argon': { effort: 'high', options: ['low', 'medium'] },
};

/**
 * The effort override to send for a slug, or undefined when the slug doesn't
 * offer it. Used by the client before sending and by the chat route, which
 * must never trust the request.
 */
export function resolveChatRequestEffort(
  slug: string | null | undefined,
  effort: unknown,
): ChatRequestEffort | undefined {
  if (!slug || typeof effort !== 'string') return undefined;
  const options = CHAT_MODEL_EFFORTS[slug as ChatModelType]?.options;
  return options?.find((option) => option === effort);
}
